/**
 * 用户认证模块 — 登录/注册/会话管理
 * CLOUD_MODE=true 时走后端 /api/auth（Cloudflare Pages Functions + D1）
 * 本地模式：localStorage 实现（含 SHA-256 防明文）
 */
import { data, replaceData } from '../shared.js';
import { getToday } from '../helpers/dates.js';
import { migrateData } from '../migrations.js';
import { CLOUD_MODE } from '../sync-manager.js';
import { CloudflareAuth } from '../adapters/cloudflare-auth.js';
import { CloudflareAdapter } from '../adapters/cloudflare.js';

const USERS_KEY = 'competitor_users';
// 密码哈希（SHA-256，异步）。避免 localStorage 明文存储口令。
function sha256(str) {
    return crypto.subtle.digest('SHA-256', new TextEncoder().encode(str))
        .then(function(buf) {
            return Array.prototype.map.call(new Uint8Array(buf), function(b) {
                return ('0' + b.toString(16)).slice(-2);
            }).join('');
        });
}
function hashPassword(pw) { return sha256('competitor-sandbox::' + pw); }
const SESSION_KEY = 'competitor_session';

// ==================== 用户 CRUD ====================

function getUsers() {
    try { return JSON.parse(localStorage.getItem(USERS_KEY)) || []; } catch(e) { return []; }
}

function saveUsers(users) {
    localStorage.setItem(USERS_KEY, JSON.stringify(users));
}

function getUserDataKey(email) {
    return 'competitorTracker_' + email.replace(/[^a-zA-Z0-9@._-]/g, '');
}

// ==================== App Shell 显示/隐藏 ====================

function showAppShell() {
    const shell = document.getElementById('appShell');
    if (shell) shell.classList.remove('u-hidden');
}

function hideAppShell() {
    const shell = document.getElementById('appShell');
    if (shell) shell.classList.add('u-hidden');
}

// ==================== 公开 API ====================

export function register(email, password, name) {
    if (!email || !password || !name) {
        window.showToast('请填写所有字段', 'error');
        return Promise.resolve(false);
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        window.showToast('邮箱格式不正确', 'error');
        return Promise.resolve(false);
    }
    if (password.length < 6) {
        window.showToast('密码至少 6 位', 'error');
        return Promise.resolve(false);
    }

    // 云端模式：注册走后端 API，成功后自动登录
    if (CLOUD_MODE) {
        return CloudflareAuth.register(email, password, name).then(function(res) {
            if (!res || !res.ok) {
                window.showToast(res && res.error ? res.error : '注册失败', 'error');
                return false;
            }
            return finishLogin({ email: email, name: res.name || name, is_admin: !!res.is_admin }, res.token);
        });
    }

    const users = getUsers();
    if (users.some(u => u.email === email)) {
        window.showToast('该邮箱已注册', 'error');
        return false;
    }

    // 密码以哈希存储，不落明文
    hashPassword(password).then(function(hash) {
        users.push({ email, password: hash, name, createdAt: Date.now() });
        saveUsers(users);
        // 创建用户独立数据
        const freshData = {
            schemaVersion: 1,
            archives: {},
            currentArchive: null,
            currentTime: 'morning',
            selectedDate: getToday(),
            selfHotel: { name: '', area: '', star: '', rooms: '', occupancy: '', efficiency: '', utilization: 0, adr: '', coverNote: '', createdAt: '', roomTypes: {}, history: {} }
        };
        localStorage.setItem(getUserDataKey(email), JSON.stringify(freshData));
        // 自动登录
        login(email, password);
    });
    return true;
}



export function login(email, password) {
    if (!email || !password) {
        window.showToast('请输入邮箱和密码', 'error');
        return Promise.resolve(false);
    }

    // 云端模式：登录走后端 API，token 存入会话
    if (CLOUD_MODE) {
        return CloudflareAuth.login(email, password).then(function(res) {
            if (!res || !res.ok) {
                window.showToast(res && res.error ? res.error : '邮箱或密码错误', 'error');
                return false;
            }
            return finishLogin({ email: email, name: res.name || email, is_admin: !!res.is_admin }, res.token);
        });
    }

    const users = getUsers();
    // 先按明文匹配（兼容旧版数据），再按哈希匹配；命中明文时顺手迁移为哈希
    const plainMatch = users.find(u => u.email === email && u.password === password);
    if (plainMatch) {
        // 兼容迁移：把明文密码升级为哈希
        hashPassword(password).then(function(hash) {
            plainMatch.password = hash;
            saveUsers(users);
        });
    }
    let user = plainMatch;
    if (!user) {
        // 异步哈希匹配
        return hashPassword(password).then(function(hash) {
            const u2 = users.find(u => u.email === email && u.password === hash);
            if (!u2) {
                window.showToast('邮箱或密码错误', 'error');
                return false;
            }
            return finishLogin(u2);
        });
    }
    return finishLogin(user);
}

async function finishLogin(user, token) {
    // 保存会话（云端模式附带 token；is_admin 供教师端入口显隐）
    const session = { email: user.email, name: user.name, loginTime: Date.now() };
    if (token) session.token = token;
    if (user.is_admin) session.is_admin = true;
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));

    // 加载用户数据（云端异步，等待完成再跳转，避免竞态丢 session）
    const loadResult = loadUserData(user.email);
    if (loadResult && typeof loadResult.then === 'function') {
        try { await loadResult; } catch(e) { console.error('loadUserData failed', e); }
    }

    // 更新头部用户名
    const headerName = document.getElementById('headerUserName');
    if (headerName) headerName.textContent = user.name || user.email;

    // 判断页面上下文：如果当前在登录页则跳转 app
    if (window.location.href.includes('login')) {
        window.location.href = 'app.html';
        return true;
    }

    // 显示主应用界面
    showAppShell();

    // 显示移动端菜单按钮
    const menuBtn = document.getElementById('mobileMenuBtn');
    if (menuBtn) menuBtn.classList.remove('u-hidden');
    const overlay = document.getElementById('mobileOverlay');
    if (overlay) overlay.classList.remove('u-hidden');

    window.showToast('欢迎回来，' + user.name, 'success');

    // 跳转首页
    setTimeout(function() { window.showPage('home'); }, 300);
    return true;
}

export function logout() {
    if (!confirm('确定退出登录？')) return;

    // 云端模式：先调用后端登出，再保存本地缓存
    if (CLOUD_MODE) {
        const session = getSessionUser();
        if (session && session.token) {
            CloudflareAuth.logout(session.token);
        }
        localStorage.removeItem(SESSION_KEY);
        replaceData({
            archives: {},
            currentArchive: null,
            currentTime: 'morning',
            selectedDate: getToday()
        });
        window.location.href = 'index.html';
        return;
    }

    // 保存当前数据
    saveCurrentUserData();

    // 清除会话
    localStorage.removeItem(SESSION_KEY);

    // 重置共享数据
    replaceData({
        archives: {},
        currentArchive: null,
        currentTime: 'morning',
        selectedDate: getToday()
    });

    // 跳转首页
    window.location.href = 'index.html';
}

export function checkSession() {
    try {
        const raw = localStorage.getItem(SESSION_KEY);
        if (!raw) return null;
        const session = JSON.parse(raw);
        if (!session || !session.email) return null;

        // 云端模式：信任本地 token（过期时接口会 401，触发重新登录）
        if (CLOUD_MODE) {
            if (!session.token) {
                localStorage.removeItem(SESSION_KEY);
                return null;
            }
            return { email: session.email, name: session.name, token: session.token, is_admin: !!session.is_admin };
        }

        const users = getUsers();
        const user = users.find(u => u.email === session.email);
        if (!user) {
            localStorage.removeItem(SESSION_KEY);
            return null;
        }
        return user;
    } catch(e) {
        localStorage.removeItem(SESSION_KEY);
        return null;
    }
}

export function getSessionUser() {
    try {
        const raw = localStorage.getItem(SESSION_KEY);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch(e) { return null; }
}

export function showLoginPage() {
    document.querySelectorAll('.page').forEach(function(p) { p.classList.remove('active'); });
    const loginPage = document.getElementById('page-login');
    if (loginPage) {
        loginPage.classList.add('active');
        loginPage.style.display = '';
        window.scrollTo(0, 0);
    }
}

// ==================== 数据加载/保存 ====================

export function loadUserData(email) {
    // 云端模式：从 /api/data 拉取（无数据时返回默认空结构）
    if (CLOUD_MODE) {
        return CloudflareAdapter.load('cloud').then(function(cloudData) {
            if (cloudData) {
                replaceData(migrateData(cloudData));
                return cloudData;
            }
            const fresh = {
                schemaVersion: 1,
                archives: {},
                currentArchive: null,
                currentTime: 'morning',
                selectedDate: getToday(),
                selfHotel: { name: '', area: '', star: '', rooms: '', occupancy: '', efficiency: '', utilization: 0, adr: '', coverNote: '', createdAt: '', roomTypes: {}, history: {} }
            };
            replaceData(fresh);
            return fresh;
        }).catch(function(e) {
            console.error('Failed to load cloud user data', e);
            return null;
        });
    }

    const key = getUserDataKey(email);
    try {
        const saved = localStorage.getItem(key);
        if (saved) {
            const parsed = JSON.parse(saved);
            replaceData(migrateData(parsed));
        } else {
            // 新用户：从旧 key 迁移或使用默认空数据
            const oldData = localStorage.getItem('competitorTracker');
            if (oldData) {
                try {
                    const parsed = JSON.parse(oldData);
                    replaceData(migrateData(parsed));
                    localStorage.setItem(key, JSON.stringify(migrateData(parsed)));
                } catch(e) {}
            }
        }
    } catch(e) {
        console.error('Failed to load user data', e);
    }
}

export function saveCurrentUserData() {
    const session = getSessionUser();
    if (!session) return;
    try {
        const key = getUserDataKey(session.email);
        localStorage.setItem(key, JSON.stringify(data));
    } catch(e) {
        console.error('Failed to save user data', e);
    }
}
